import React, { useState, useEffect } from 'react';
import {
  Receipt, Search, Filter, Download, Eye, CheckCircle,
  Clock, AlertCircle, Plus, Calendar, DollarSign, Users,
  X, Phone, Mail, MapPin, FileText, History, User
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';

const AccountsReceivableTab = () => {
  const { language, t } = useLanguage();
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showReceiptForm, setShowReceiptForm] = useState(false);

  const customers = [
    {
      id: 'C-001',
      name: 'Retail Store Chain A',
      balance: 650000,
      current: 450000,
      days30: 150000,
      days60: 50000,
      days90: 0,
      lastPayment: '2024-02-08',
      status: 'active'
    },
    {
      id: 'C-002',
      name: 'Wholesale Distributor B',
      balance: 420000,
      current: 280000,
      days30: 100000,
      days60: 40000,
      days90: 0,
      lastPayment: '2024-02-05',
      status: 'active'
    },
    {
      id: 'C-003',
      name: 'Corporate Client C',
      balance: 285000,
      current: 200000,
      days30: 65000,
      days60: 20000,
      days90: 0,
      lastPayment: '2024-02-10',
      status: 'active'
    },
    {
      id: 'C-004',
      name: 'Online Retailer D',
      balance: 195000,
      current: 140000,
      days30: 40000,
      days60: 15000,
      days90: 0,
      lastPayment: '2024-02-01',
      status: 'active'
    },
    {
      id: 'C-005',
      name: 'Franchise Group E',
      balance: 145000,
      current: 100000,
      days30: 30000,
      days60: 15000,
      days90: 0,
      lastPayment: '2024-01-28',
      status: 'active'
    }
  ];

  const invoices = [
    {
      id: 'INV-AR-2024-001',
      customer: 'Retail Store Chain A',
      customerId: 'C-001',
      invoiceNumber: 'INV-2024-001',
      date: '2024-02-10',
      dueDate: '2024-03-10',
      amount: 185000,
      paid: 0,
      balance: 185000,
      status: 'open',
      daysOverdue: 0,
      aging: 'current'
    },
    {
      id: 'INV-AR-2024-002',
      customer: 'Wholesale Distributor B',
      customerId: 'C-002',
      invoiceNumber: 'INV-2024-002',
      date: '2024-01-28',
      dueDate: '2024-02-27',
      amount: 125000,
      paid: 0,
      balance: 125000,
      status: 'open',
      daysOverdue: 15,
      aging: '1-30'
    },
    {
      id: 'INV-AR-2024-003',
      customer: 'Corporate Client C',
      customerId: 'C-003',
      invoiceNumber: 'INV-2024-003',
      date: '2024-01-20',
      dueDate: '2024-02-19',
      amount: 95000,
      paid: 0,
      balance: 95000,
      status: 'open',
      daysOverdue: 23,
      aging: '31-60'
    },
    {
      id: 'INV-AR-2024-004',
      customer: 'Online Retailer D',
      customerId: 'C-004',
      invoiceNumber: 'INV-2024-004',
      date: '2024-01-12',
      dueDate: '2024-02-11',
      amount: 65000,
      paid: 0,
      balance: 65000,
      status: 'open',
      daysOverdue: 31,
      aging: '31-60'
    },
    {
      id: 'INV-AR-2024-005',
      customer: 'Franchise Group E',
      customerId: 'C-005',
      invoiceNumber: 'INV-2024-005',
      date: '2023-12-15',
      dueDate: '2024-01-14',
      amount: 35000,
      paid: 0,
      balance: 35000,
      status: 'open',
      daysOverdue: 58,
      aging: '61-90'
    }
  ];

  const totalReceivables = customers.reduce((sum, c) => sum + c.balance, 0);
  const overdueAmount = invoices
    .filter(inv => inv.daysOverdue > 0)
    .reduce((sum, inv) => sum + inv.balance, 0);

  const getAgingColor = (aging) => {
    switch (aging) {
      case 'current': return 'text-green-600 bg-green-50';
      case '1-30': return 'text-yellow-600 bg-yellow-50';
      case '31-60': return 'text-orange-600 bg-orange-50';
      case '61-90': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Get customer details (mock data)
  const getCustomerDetails = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    if (!customer) return null;

    return {
      ...customer,
      contact: {
        name: 'Mohammed Al-Rashid',
        email: 'contact@' + customer.name.toLowerCase().replace(/\s+/g, '') + '.com',
        phone: '+966 50 987 6543',
        address: 'King Abdullah Road, Jeddah 21432, Saudi Arabia'
      },
      totalSales: customer.balance * 4.2, // Mock calculation
      salesHistory: invoices.filter(inv => inv.customerId === customerId).map(inv => ({
        ...inv,
        saleDate: inv.date,
        items: [
          { description: 'Product Sales', quantity: 50, unitPrice: inv.amount / 50 }
        ]
      })),
      files: [
        { id: '1', name: 'Credit_Application.pdf', type: 'pdf', date: '2024-01-20', size: '1.8 MB' },
        { id: '2', name: 'Contract_2024.pdf', type: 'pdf', date: '2024-01-15', size: '2.1 MB' }
      ],
      paymentHistory: [
        { date: '2024-02-08', amount: 450000, method: 'Bank Transfer', reference: 'REC-2024-001' },
        { date: '2024-01-25', amount: 320000, method: 'Check', reference: 'CHK-2024-002' },
        { date: '2024-01-10', amount: 280000, method: 'Bank Transfer', reference: 'REC-2023-045' }
      ]
    };
  };

  const handleCustomerClick = (customer) => {
    const details = getCustomerDetails(customer.id);
    setSelectedCustomer(details);
    setShowCustomerModal(true);
  };

  const filteredInvoices = invoices.filter(inv => {
    if (filter === 'all') return true;
    if (filter === 'overdue') return inv.daysOverdue > 0;
    if (filter === 'current') return inv.aging === 'current';
    return inv.aging === filter;
  });

  return (
    <div className={`space-y-6 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('totalReceivables')}</p>
            <Receipt className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">SAR {totalReceivables.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('overdue')}</p>
            <AlertCircle className="h-5 w-5 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-red-600">SAR {overdueAmount.toLocaleString()}</p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('current')}</p>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-600">
            SAR {(totalReceivables - overdueAmount).toLocaleString()}
          </p>
        </div>
        <div className={`bg-white rounded-xl p-5 border border-gray-200 shadow-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between mb-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <p className="text-sm font-medium text-gray-600">{t('customers')}</p>
            <Users className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{customers.length}</p>
        </div>
      </div>

      {/* Filters and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${language === 'ar' ? 'right-3' : 'left-3'}`} />
            <input
              type="text"
              placeholder={t('searchCustomersInvoices')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                language === 'ar' ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'
              }`}
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white ${language === 'ar' ? 'text-right' : 'text-left'}`}
          >
            <option value="all">{t('allInvoices')}</option>
            <option value="current">{t('current')}</option>
            <option value="1-30">{t('days30')}</option>
            <option value="31-60">{t('days60')}</option>
            <option value="61-90">{t('days90')}</option>
            <option value="overdue">{t('overdue')}</option>
          </select>
        </div>
        <div className={`flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <button 
            onClick={() => alert(t('exportFunctionality'))}
            className={`px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Download className="h-4 w-4" />
            {t('export')}
          </button>
          <button 
            onClick={() => setShowReceiptForm(true)}
            className={`px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 ${language === 'ar' ? 'flex-row-reverse' : ''}`}
          >
            <Plus className="h-4 w-4" />
            {t('recordReceiptButton')}
          </button>
        </div>
      </div>

      {/* Customers Summary */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className={`p-5 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <h3 className="text-lg font-semibold text-gray-900">{t('customerSummary')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <thead>
              <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('customer')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('totalBalance')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('current')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('days30')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('days60')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('days90')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('lastPayment')}</th>
                <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="font-medium text-gray-900">{customer.name}</p>
                      <p className="text-xs text-gray-500">{customer.id}</p>
                    </div>
                  </td>
                  <td className={`py-3 px-4 font-semibold text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {customer.balance.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {customer.current.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {customer.days30.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {customer.days60.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-gray-700 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {customer.days90.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{customer.lastPayment}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => handleCustomerClick(customer)}
                        className="p-1.5 hover:bg-gray-200 rounded"
                      >
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoices List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className={`p-5 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <h3 className="text-lg font-semibold text-gray-900">{t('outstandingInvoices')}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className={`w-full text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
            <thead>
              <tr className="text-xs font-semibold text-gray-600 uppercase border-b border-gray-200 bg-gray-50">
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('invoiceNumber')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('customer')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('date')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('dueDate')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('amount')}</th>
                <th className={`py-3 px-4 ${language === 'ar' ? 'text-left' : 'text-right'}`}>{t('balance')}</th>
                <th className={`py-3 px-4 text-center`}>{t('aging')}</th>
                <th className={`py-3 px-4 text-center`}>{t('status')}</th>
                <th className={`py-3 px-4 text-center`}>{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className={`py-3 px-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                    <div className={language === 'ar' ? 'text-right' : 'text-left'}>
                      <p className="font-medium text-gray-900">{invoice.invoiceNumber}</p>
                      <p className="text-xs text-gray-500">{invoice.id}</p>
                    </div>
                  </td>
                  <td className={`py-3 px-4 text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{invoice.customer}</td>
                  <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{invoice.date}</td>
                  <td className={`py-3 px-4 text-gray-600 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{invoice.dueDate}</td>
                  <td className={`py-3 px-4 font-medium text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {invoice.amount.toLocaleString()}
                  </td>
                  <td className={`py-3 px-4 font-semibold text-gray-900 ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                    SAR {invoice.balance.toLocaleString()}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getAgingColor(invoice.aging)}`}>
                        {invoice.daysOverdue > 0 ? `${invoice.daysOverdue} ${t('days')}` : invoice.aging}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      invoice.status === 'open' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {invoice.status === 'open' ? t('open') : t('paid')}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <button 
                        onClick={() => {
                          const customer = customers.find(c => c.id === invoice.customerId);
                          if (customer) handleCustomerClick(customer);
                        }}
                        className="p-1.5 hover:bg-gray-200 rounded"
                      >
                        <Eye className="h-4 w-4 text-gray-600" />
                      </button>
                      {invoice.status === 'open' && (
                        <button 
                          onClick={() => {
                            setShowReceiptForm(true);
                            setSelectedCustomer({ id: invoice.customerId, name: invoice.customer, invoiceId: invoice.id });
                          }}
                          className="p-1.5 hover:bg-green-100 rounded"
                        >
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Customer Detail Modal */}
      <AnimatePresence>
        {showCustomerModal && selectedCustomer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCustomerModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedCustomer.name}</h2>
                    <p className="text-sm text-gray-600 mt-1">{t('customerID')}: {selectedCustomer.id}</p>
                  </div>
                  <button
                    onClick={() => setShowCustomerModal(false)}
                    className="p-2 hover:bg-white rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-green-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">{t('outstandingBalance')}</p>
                    <p className="text-2xl font-bold text-green-600">
                      SAR {selectedCustomer.balance.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">{t('totalSales')}</p>
                    <p className="text-2xl font-bold text-blue-600">
                      SAR {selectedCustomer.totalSales.toLocaleString()}
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">{t('openInvoices')}</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {selectedCustomer.salesHistory.length}
                    </p>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">{t('lastPayment')}</p>
                    <p className="text-lg font-bold text-orange-600">
                      {selectedCustomer.lastPayment}
                    </p>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {t('contactInformation')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start gap-3">
                        <User className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">{t('contactPerson')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedCustomer.contact.name}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">{t('email')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedCustomer.contact.email}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">{t('phone')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedCustomer.contact.phone}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-600">{t('address')}</p>
                          <p className="text-sm font-medium text-gray-900">{selectedCustomer.contact.address}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sales History */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Sales History
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Invoice #</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Due Date</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700">Balance</th>
                          <th className="text-center py-3 px-4 font-semibold text-gray-700">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCustomer.salesHistory.map((sale) => (
                          <tr key={sale.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4">
                              <button className="text-blue-600 hover:text-blue-700 font-medium">
                                {sale.invoiceNumber}
                              </button>
                            </td>
                            <td className="py-3 px-4 text-gray-600">{sale.saleDate}</td>
                            <td className="py-3 px-4 text-gray-600">{sale.dueDate}</td>
                            <td className="py-3 px-4 text-right text-gray-900">
                              SAR {sale.amount.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold text-gray-900">
                              SAR {sale.balance.toLocaleString()}
                            </td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 text-xs font-medium rounded ${
                                sale.status === 'open' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {sale.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment History */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    {t('paymentHistory')}
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Date</th>
                          <th className="text-right py-3 px-4 font-semibold text-gray-700">Amount</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Method</th>
                          <th className="text-left py-3 px-4 font-semibold text-gray-700">Reference</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedCustomer.paymentHistory.map((payment, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-gray-600">{payment.date}</td>
                            <td className="py-3 px-4 text-right font-semibold text-green-600">
                              SAR {payment.amount.toLocaleString()}
                            </td>
                            <td className="py-3 px-4 text-gray-900">{payment.method}</td>
                            <td className="py-3 px-4">
                              <button className="text-blue-600 hover:text-blue-700 font-medium">
                                {payment.reference}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Files */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Files & Documents
                  </h3>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="space-y-2">
                      {selectedCustomer.files.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{file.name}</p>
                              <p className="text-xs text-gray-500">{file.size} • {file.date}</p>
                            </div>
                          </div>
                          <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                            {t('download')}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowCustomerModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowCustomerModal(false);
                    setShowReceiptForm(true);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                >
                  {t('recordPayment')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Receipt Form Modal */}
      <AnimatePresence>
        {showReceiptForm && (
          <ReceiptFormModal
            isOpen={showReceiptForm}
            onClose={() => {
              setShowReceiptForm(false);
              setSelectedCustomer(null);
            }}
            customer={selectedCustomer}
            customers={customers}
            invoices={invoices}
            onSave={(receipt) => {
              console.log('Saving receipt:', receipt);
              setShowReceiptForm(false);
              setSelectedCustomer(null);
              alert(t('paymentReceiptRecordedSuccessfully'));
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Receipt Form Modal Component
const ReceiptFormModal = ({ isOpen, onClose, customer, customers, invoices, onSave }) => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    customerId: customer?.id || '',
    invoiceId: customer?.invoiceId || '',
    date: new Date().toISOString().split('T')[0],
    amount: customer?.invoiceId ? invoices.find(inv => inv.id === customer.invoiceId)?.balance || '' : '',
    paymentMethod: 'Bank Transfer',
    reference: '',
    notes: ''
  });

  if (!isOpen) return null;

  const selectedCustomerInvoices = invoices.filter(inv => inv.customerId === formData.customerId && inv.status === 'open');

  const handleSave = () => {
    if (!formData.customerId || !formData.amount || !formData.date) {
      alert(t('pleaseFillAllFields'));
      return;
    }
    onSave(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className={`p-6 border-b border-gray-200 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div className={`flex items-center justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
            <h2 className="text-2xl font-bold text-gray-900">{t('recordReceipt')}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('customer')} *</label>
            <select
              value={formData.customerId}
              onChange={(e) => {
                setFormData({ ...formData, customerId: e.target.value, invoiceId: '' });
              }}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            >
              <option value="">{t('selectCustomer')}</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {formData.customerId && (
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('invoiceOptional')}</label>
              <select
                value={formData.invoiceId}
                onChange={(e) => {
                  const invoice = invoices.find(inv => inv.id === e.target.value);
                  setFormData({ 
                    ...formData, 
                    invoiceId: e.target.value,
                    amount: invoice ? invoice.balance : formData.amount
                  });
                }}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              >
                <option value="">{t('selectInvoiceOrLeaveBlank')}</option>
                {selectedCustomerInvoices.map(inv => (
                  <option key={inv.id} value={inv.id}>
                    {inv.invoiceNumber} - {t('balanceSAR')} {inv.balance.toLocaleString()}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('paymentDate')} *</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('amountSAR')} *</label>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('paymentMethod')}</label>
              <select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              >
                <option value={t('bankTransfer')}>{t('bankTransfer')}</option>
                <option value={t('check')}>{t('check')}</option>
                <option value={t('cash')}>{t('cash')}</option>
                <option value={t('creditCard')}>{t('creditCard')}</option>
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('referenceNumber')}</label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                placeholder="REC-2024-001"
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium text-gray-700 mb-1 ${language === 'ar' ? 'text-right' : 'text-left'}`}>{t('notes')}</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder={t('additionalNotes')}
              rows={3}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}
            />
          </div>
        </div>

        <div className={`p-6 border-t border-gray-200 bg-gray-50 flex items-center gap-3 ${language === 'ar' ? 'flex-row-reverse justify-start' : 'justify-end'}`}>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
          >
            {t('recordReceiptButton')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AccountsReceivableTab;
